/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice List Page - Displays user's focused practice sessions (UC-116)
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'
import {
  Target,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  PlayCircle,
  Pause,
  XCircle,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  Calendar,
  GraduationCap
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import FocusedPracticeEmptyState from '../components/FocusedPracticeEmptyState'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'

const FocusedPracticeList = () => {
  const navigate = useNavigate()
  const {
    sessions,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    filters,
    fetchSessions,
    setFilters,
    setPage
  } = useFocusedPracticeStore()

  const [searchInput, setSearchInput] = useState('')

  // Fill the viewport so the sessions list scrolls internally (header/filters stay pinned above,
  // pagination pinned below) instead of leaving an empty band under a short list.
  const listFillRef = useRef(null)
  const listFillHeight = useFillViewportHeight(listFillRef, {
    enabled: sessions.length > 0
  })

  useEffect(() => {
    loadSessions()
  }, [currentPage, filters.status])

  const loadSessions = async () => {
    const result = await fetchSessions()
    if (!result.success) {
      toast.error(getErrorMessage(result.error))
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setFilters({ search: searchInput })
    loadSessions()
  }

  const handleStatusFilter = (status) => {
    setFilters({ status: status === filters.status ? null : status })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        label: 'Pending',
        className:
          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
        icon: Clock
      },
      pre_practice: {
        label: 'Reviewing',
        className:
          'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700',
        icon: PlayCircle
      },
      in_progress: {
        label: 'In Progress',
        className:
          'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700',
        icon: PlayCircle
      },
      completed: {
        label: 'Completed',
        className:
          'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700',
        icon: CheckCircle2
      },
      abandoned: {
        label: 'Abandoned',
        className:
          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-600',
        icon: XCircle
      }
    }

    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.className}`}
      >
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    )
  }

  const getImprovementBadge = (before, after) => {
    if (before === null || after === null) return null

    const improvement = after - before
    if (improvement > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-sm font-medium">
          <TrendingUp className="h-4 w-4" />+{improvement}%
        </span>
      )
    } else if (improvement < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-sm font-medium">
          <TrendingUp className="h-4 w-4 rotate-180" />
          {improvement}%
        </span>
      )
    }
    return <span className="text-gray-500 dark:text-gray-400 text-sm">No change</span>
  }

  const handleSessionClick = (session) => {
    // Always navigate to the review page first
    // The review page will handle routing based on session status
    navigate(`/focused-practice/${session.id}/review`)
  }

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'pre_practice', label: 'Reviewing' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ]

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <Target className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Focused Practice
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Track your targeted practice sessions
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search by name or domain..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <div className="flex gap-2 flex-wrap">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusFilter(option.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    filters.status === option.value
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <p className="text-red-600 dark:text-red-400">{getErrorMessage(error)}</p>
          <button
            onClick={loadSessions}
            className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : sessions.length === 0 ? (
        <FocusedPracticeEmptyState onStartChallenge={() => navigate('/challenge-type')} />
      ) : (
        // Filled to the viewport so the list scrolls internally while pagination stays pinned below.
        <div
          ref={listFillRef}
          className="flex flex-col gap-4"
          style={listFillHeight ? { height: listFillHeight } : undefined}
        >
          <div className="space-y-4 flex-1 overflow-auto min-h-0">
            {sessions.map((session) => (
              <div
                key={session.id}
                onClick={() => handleSessionClick(session)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 cursor-pointer hover:shadow-md hover:border-primary-200 dark:hover:border-primary-700 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {session.name}
                      </h3>
                      {getStatusBadge(session.status)}
                      {session.sourceTutorPractice && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                          <GraduationCap className="h-3 w-3" /> From your tutor
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <Target className="h-4 w-4" />
                        <span>{session.targetDomain}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(session.createdAt)}</span>
                      </div>
                      {(() => {
                        // Tutor-assigned (homework) sessions have no "before" accuracy — show just the score.
                        const isTutor = !!session.sourceTutorPractice
                        const before = session.beforeAccuracy ?? session.sourceAccuracy
                        if (session.status === 'completed') {
                          if (isTutor || before == null) {
                            return session.afterAccuracy != null ? (
                              <span>Score: {Math.round(session.afterAccuracy)}%</span>
                            ) : null
                          }
                          return (
                            <div className="flex items-center gap-3">
                              <span>Before: {before}%</span>
                              <span>After: {session.afterAccuracy}%</span>
                              {getImprovementBadge(before, session.afterAccuracy)}
                            </div>
                          )
                        }
                        return !isTutor && before != null ? (
                          <span>Source accuracy: {before}%</span>
                        ) : null
                      })()}
                    </div>
                  </div>

                  <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination — pinned below the scrolling list inside the fill region. */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between shrink-0">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing {sessions.length} of {totalCount} sessions
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FocusedPracticeList
